const ALLOWED_ABSOLUTE_CONTENT_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];

/**
 * Restricts CMS-authored links to web, email, phone, or same-site relative URLs.
 * The renderer repeats this check so stale/legacy CMS data cannot emit a
 * javascript: URL even if it predates author-time validation.
 */
export function isSafeFormFunnelContentHref(value: string | null | undefined): boolean {
  const href = value?.trim();
  if (!href || /[\\\u0000-\u0020\u007f]/.test(href)) return false;

  if (href.startsWith("/") && !href.startsWith("//")) return true;

  try {
    return ALLOWED_ABSOLUTE_CONTENT_PROTOCOLS.includes(new URL(href).protocol);
  } catch {
    return false;
  }
}

export function validateFormFunnelContentHref(value: string | null | undefined): true | string {
  if (!value) return true;
  return (
    isSafeFormFunnelContentHref(value) ||
    "Use a relative path, http(s) URL, email address (mailto:), or phone number (tel:)."
  );
}

/** Empty paragraphs and formatting-only nodes do not replace legacy copy. */
export function hasFormFunnelRichTextContent(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const node = value as Record<string, unknown>;
  if (node.root) return hasFormFunnelRichTextContent(node.root);
  if (node.type === "text") return typeof node.text === "string" && Boolean(node.text.trim());
  // Media and other non-text content must also survive the plain-text fallback.
  if (node.type === "upload") return Boolean(node.value);
  if (node.type === "horizontalrule" || node.type === "block" || node.type === "inlineBlock") {
    return true;
  }
  return Array.isArray(node.children) && node.children.some(hasFormFunnelRichTextContent);
}

export function validateFormFunnelTextContent(
  value: unknown,
  { siblingData }: { siblingData?: { content?: string | null } }
): true | string {
  return hasFormFunnelRichTextContent(value) || Boolean(siblingData?.content?.trim())
    ? true
    : "Text blocks must have rich content";
}
